import React, { useMemo } from 'react';
import clsx from 'clsx';

const OptionValue = ({ option, optionId, selectedOption, value, selectedOptions, variants, onChange }) => {
  const selectedItemOptions = selectedOptions.map((o) => (o.name === option.name ? { name: option.name, value } : o));
  const newVariant = variants.find((v) => JSON.stringify(v.selectedOptions) === JSON.stringify(selectedItemOptions));
  const isChecked = selectedOption?.value === value;
  const handleKeyPress = (_option, _value, code) => (code === 'Enter' || code === 'Space') && onChange(_option, _value);
  const isAvailable = newVariant && newVariant.availableForSale && newVariant.inventoryQuantity > 10;

  return (
    <label
      key={`${optionId}-${value}`}
      htmlFor={`${optionId}-${value.replace(' ', '')}`}
      className={`relative`}
    >
      <input
        type="radio"
        id={`${optionId}-${value.replace(' ', '')}`}
        name={`${optionId}-${option.name}`}
        value={value}
        checked={isChecked}
        aria-checked={isChecked}
        onChange={() => onChange(option, value)}
        className={clsx('peer sr-only', {
          unavailable: !isAvailable,
        })}
      />
      <div
        className={clsx(
          'duration-250 flex size-7.5 cursor-pointer items-center justify-center rounded-full border border-grey-lighter bg-grey-lighter text-12 transition-colors peer-checked:border-black ',
          {
            'text-grey-dark': !isAvailable,
            'hover:border-brand-tangerine peer-checked:hover:border-brand-tangerine': isAvailable,
          },
        )}
        tabIndex="0"
        role="radio"
        onKeyDown={({ code }) => handleKeyPress(option, value, code)}
        aria-checked={isChecked}
      >
        {value}
      </div>
      {!isAvailable && <div className="absolute left-4 top-0 -ml-px h-full w-px rotate-45 bg-grey-dark"></div>}
    </label>
  );
};
const RadioOption = ({ option, selectedOptions, ...props }) => {
  const optionId = `product-option-${option.name}`;

  const selectedOption = useMemo(() => selectedOptions.find((o) => o.name === option.name), [selectedOptions, option]);

  const optionValues = useMemo(() => {
    const colorOption = selectedOptions.find((o) => o.name.toLowerCase().includes('color'));
    const variantSizes = props.variants
      .filter((variant) => {
        if (colorOption) {
          return variant.selectedOptions.some((o) => o.value === colorOption.value && o.name === colorOption.name);
        }
        return true;
      })
      .map((variant) => variant.selectedOptions.find((o) => o.name.toLowerCase().includes('size')).value);

    return option.values.filter((value) => {
      return variantSizes.includes(value);
    });
  }, [selectedOptions, props.variants, option]);

  return (
    <div>
      <label
        htmlFor={optionId}
        className="body-2 mb-2 block cursor-pointer"
      >
        {option.name}: {!!selectedOption?.value && <span className="body-2-bold">{selectedOption.value}</span>}
      </label>

      <div
        id={optionId}
        name={optionId}
        className="flex flex-wrap gap-3 focus:border-indigo-500 focus:outline-none focus:ring-indigo-500"
        data-option-name={option.name}
        role="radiogroup"
        aria-labelledby="product-option-size-label"
      >
        <span
          id="product-option-size-label"
          className="sr-only"
        >
          Size
        </span>
        {optionValues.map((value) => (
          <OptionValue
            key={value}
            option={option}
            value={value}
            selectedOptions={selectedOptions}
            selectedOption={selectedOption}
            optionId={optionId}
            {...props}
          />
        ))}
      </div>
    </div>
  );
};

export default RadioOption;
